import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { ScuiModalComponent } from '@sc-ui';
import { texts } from './requirements-editor.texts';

@Component({
    selector: 'sc-requirements-editor',
    templateUrl: './requirements-editor.component.html',
    styleUrls: ['./requirements-editor.component.scss'],
    standalone: false
})
export class RequirementsEditorComponent implements OnChanges {

  ngOnChanges(changes: any): void {
    this.sort();
  }

  private readonly MUST_HAVE_PRIORITIES = ['MustHave', 'Core'];

  @Input()
  language: 'de' | 'en' = 'de';

  texts = texts;

  @Input()
  project : any;

  @Output()
  change = new EventEmitter<any>();

  @Output()
  onAdd = new EventEmitter<any>();

  @ViewChild('editRequirementModal')
  editRequirementModal: ScuiModalComponent;

  @ViewChild('editExperienceModal')
  editExperienceModal: ScuiModalComponent;

 @ViewChild('editLanguageModal')
  editLanguageModal: ScuiModalComponent;

  @ViewChild('editCustomModal')
  editCustomModal: ScuiModalComponent;

  @Input()
  readonly = false;

  @Input()
  hideInvisible = false;

  @Input()
  isMustHaveTypeEditable = false;

  briefingChat: any;

  @Input()
  isInEditMode = false;

  get isMenuVisible()
  {
    return this.project.Profile.Requirements.some(r => r.isSelected) || this.isInEditMode;
  }

  get canJoin() {
    var selected = this.project.Profile.Requirements.filter(req => req.isSelected);
    if (selected.length < 2) return false;

    var joinableTypes = ['Skill', 'Industry', 'Degree']

    return joinableTypes.some(type =>
      selected.every(req => req.Type === type)
    );

  }

  selectedRequirement: any;
  selectedAlternatives: any;

  addAlternative() {
    this.selectedAlternatives.push({ value: '' });
  }

  removeAlternative(index: number) {
    if (this.selectedAlternatives.length > 1) {
      this.selectedAlternatives.splice(index, 1);
    }
  }

  get canEdit() {
    return this.project.Profile.Requirements.filter(r => r.isSelected).length == 1;
  }

  edit() {
    if (!this.canEdit) return;

    const selected = this.project.Profile.Requirements.filter(req => req.isSelected)[0];
    this.selectedRequirement = selected;
    this.selectedAlternatives = selected.Alternatives.map((a: string) => ({ value: a }));

    if (selected.Type == 'Experience')
    {
      this.editExperienceModal.show();
      return;
    }

    if (selected.Type == 'Language')
    {
      this.editLanguageModal.show();
      return;
    }

    if (selected.Type == 'Custom')
    {
      if (!selected.Alternatives || selected.Alternatives.length == 0) selected.Alternatives = [''];
      this.editCustomModal.show();
      return;
    }
    
    
    this.editRequirementModal.show();
  }

  saveRequirement() {
    const filteredValues = [...new Set(
      this.selectedAlternatives
        .map(a => a.value?.trim())
        .filter(val => val && val.length > 0)
    )];

    if (filteredValues.length === 0) {
      this.project.Profile.Requirements = this.project.Profile.Requirements.filter(req => req !== this.selectedRequirement);
    } else {
      this.selectedRequirement.Alternatives = filteredValues;
    }

    this.editRequirementModal.dismiss();
    this.editExperienceModal.dismiss();
    this.editLanguageModal.dismiss();
    this.editCustomModal.dismiss();
    this.onChange();
  }

  get canSplit() {
    return this.project.Profile.Requirements.some(r => r.isSelected && r.Alternatives?.length > 1);
  }

  join() {
    const selected = this.project.Profile.Requirements.filter(req => req.isSelected);
    if (!this.canJoin) return;

    const isAnyNiceToHave = selected.some(req => req.Priority === 'NiceToHave');
    const newPriority = isAnyNiceToHave ? 'NiceToHave' : 'MustHave';

    const mergedAlternatives = [...new Set(
      selected.reduce((acc, curr) => [...acc, ...curr.Alternatives], [] as string[])
    )];

    const joinedRequirement = {
      Type: selected[0].Type,
      Priority: newPriority,
      Alternatives: mergedAlternatives,
      isSelected: true
    };

    this.project.Profile.Requirements = [
      ...this.project.Profile.Requirements.filter(req => !req.isSelected),
      joinedRequirement
    ];

    this.sort();
    this.onChange();
  }

  split() {
    const newRequirements: any[] = [];

    this.project.Profile.Requirements.forEach(req => {

      if (req.isSelected && req.Alternatives?.length > 1) {
        req.Alternatives.forEach((alt: string) => {
          newRequirements.push({
            Type: req.Type,
            Priority: req.Priority,
            Alternatives: [alt],
            isSelected: false
          });
        });
      } else {
        newRequirements.push(req);
      }
    });

    this.project.Profile.Requirements = newRequirements;
    this.sort();
    this.onChange();
  }

  get canConvertToMustHave(): boolean {
    const selected = this.project.Profile.Requirements.filter(req => req.isSelected);
    return selected.some(r => r.Priority === 'NiceToHave');
  }

  convertToMustHave() {
    this.project.Profile.Requirements.forEach(r => {
      if (r.isSelected) {
        r.Priority = 'MustHave';
      }
    });

    this.onChange();
  }

  get canConvertToNiceToHave(): boolean {
    const selected = this.project.Profile.Requirements.filter(req => req.isSelected);
    return selected.some(r => this.MUST_HAVE_PRIORITIES.includes(r.Priority));
  }

  convertToNiceToHave() {
    this.project.Profile.Requirements.forEach(r => {
      if (r.isSelected) {
        r.Priority = 'NiceToHave';
      }
    });

    this.onChange();
  }

  get canEditFlags(): boolean {
    return this.isMustHaveTypeEditable;
  }

  get canEditMustHaveType(): boolean {
    return this.canEditFlags
      && this.selectedRequirement?.Type === 'Skill'
      && this.MUST_HAVE_PRIORITIES.includes(this.selectedRequirement?.Priority);
  }

  get canDelete()
  {
    const selected = this.project.Profile.Requirements.filter(req => req.isSelected);
    return  selected.length > 0;
  }

  delete() {
    if (!this.canDelete) return;
    this.project.Profile.Requirements = this.project.Profile.Requirements.filter(r => !r.isSelected);
    this.onChange();
  }

  private readonly TYPE_ORDER: { [key: string]: number } = {
    'Skill': 1,
    'Industry': 2,
    'Degree': 3,
    'Experience': 4,
    'Language': 5,
    'Custom': 6
  };

  private readonly PRIORITY_ORDER: { [key: string]: number } = {
    'Core': 0,
    'MustHave': 100,
    'NiceToHave': 200
  };

  sort() {
    this.project.Profile.Requirements.sort((a, b) => {
      var priorityA = this.PRIORITY_ORDER[a.Priority] + this.TYPE_ORDER[a.Type];
      var priorityB = this.PRIORITY_ORDER[b.Priority] + this.TYPE_ORDER[b.Type];

      if (isNaN(priorityA)) priorityA = 1000;
      if (isNaN(priorityB)) priorityB = 1000;

      return priorityA - priorityB;
    });
  }

  setLanguage(r, l)
  {
    r.Alternatives = [ l ];
    console.log(l)
  }

  onChange()
  {   
    this.sort(); 
    this.change.next();
  }

}
